import React, { useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useGetOrderTimeline } from '../../../hooks/orders/useGetOrderTimeline';
import useCurrency from '../../../hooks/common/useCurrency';
import { needsLatePaymentManualRefund } from '../../../utils/orderHelpers';
import RefundModal from './RefundModal';

/**
 * Sprint B (DA-2) — banner shown on cancelled orders whose payment landed
 * AFTER the pending-orders cron swept them. The customer was charged but
 * their order is cancelled; the admin needs to issue a refund manually.
 * The Issue Refund Now button reuses the existing RefundModal so we don't
 * fork the refund flow.
 */
const LateRefundBanner = ({ order }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();
    const { events } = useGetOrderTimeline(order?.id, { limit: 100 });
    const [refundOpen, setRefundOpen] = useState(false);

    const show = useMemo(
        () => needsLatePaymentManualRefund(order, events),
        [order, events]
    );

    if (!show) return null;

    // Surface the most recent completed payment amount so the admin sees the
    // figure they're about to refund without scrolling to the payment card.
    const payments = Array.isArray(order?.payments) ? order.payments : [];
    const paid = [...payments]
        .reverse()
        .find((p) => ['paid', 'completed', 'partially_refunded'].includes(p?.status));
    const amountText = paid ? formatPrice(paid.amount, paid.currency || order.currency) : '';

    return (
        <>
            <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: 2, alignItems: 'flex-start' }}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        onClick={() => setRefundOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isArabic ? 'إصدار استرداد الآن' : 'Issue Refund Now'}
                    </Button>
                }
            >
                <AlertTitle sx={{ fontWeight: 700 }}>
                    {isArabic
                        ? 'تم استلام الدفع بعد انتهاء صلاحية الطلب — يلزم استرداد'
                        : 'Payment received after expiry — refund required'}
                </AlertTitle>
                <Typography variant="body2">
                    {isArabic
                        ? `تم تأكيد دفعة العميل${amountText ? ` بمبلغ ${amountText}` : ''} من قِبل بوابة الدفع بعد إلغاء هذا الطلب تلقائياً. أصدر استرداداً.`
                        : `A customer payment${amountText ? ` of ${amountText}` : ''} was confirmed by the payment gateway AFTER this order was auto-cancelled. Issue a refund.`}
                </Typography>
            </Alert>

            <RefundModal
                open={refundOpen}
                order={order}
                onClose={() => setRefundOpen(false)}
            />
        </>
    );
};

export default LateRefundBanner;
